import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { usePageTitle } from '../context/PageTitleContext';
import './SalesTracking.scss';

const SalesTracking = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [totalSales, setTotalSales] = useState(0);
  const { user } = useSelector(state => state.auth);
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();

  // Set page title
  useEffect(() => {
    setPageTitle('Sales Tracking');
  }, [setPageTitle]);

  // Check if user is admin, if not redirect
  useEffect(() => {
    if (user && user.userRole !== 'admin') {
      console.log('Non-admin user attempted to access Sales Tracking page');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!user?.userRole === 'admin') return;
      
      try {
        setLoading(true);
        const usersCollection = collection(db, 'fredoRegisteredUsers');
        const usersSnapshot = await getDocs(usersCollection);
        const usersList = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersList);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again.');
        setLoading(false);
      }
    };

    fetchUsers();
  }, [user]);

  // Fetch all orders when no user is selected or fetch orders for a specific user
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const ordersCollection = collection(db, 'orders');
        let ordersQuery;
        
        if (selectedUser) {
          // Fetch orders for selected user
          ordersQuery = query(
            ordersCollection, 
            where('userId', '==', selectedUser),
            orderBy('createdAt', 'desc')
          );
        } else {
          // Fetch all orders when no user is selected
          ordersQuery = query(
            ordersCollection,
            orderBy('createdAt', 'desc')
          );
        }
        
        const ordersSnapshot = await getDocs(ordersQuery);
        const ordersList = ordersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setOrders(ordersList);
        
        // Calculate total sales
        const total = ordersList.reduce((sum, order) => {
          if (order.status === 'completed' || order.status === 'complete') {
            return sum + (order.totalPrice || 0);
          }
          return sum;
        }, 0);
        
        setTotalSales(total);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders. Please try again.');
        setLoading(false);
      }
    };

    fetchOrders();
  }, [selectedUser]);

  const handleUserChange = (e) => {
    setSelectedUser(e.target.value);
  };
  
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'complete':
      case 'completed':
        return 'status-complete';
      case 'pending':
        return 'status-pending';
      case 'failed':
        return 'status-failed';
      default:
        return '';
    }
  };
  
  // Filter orders based on selected filter
  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'completed') return order.status === 'completed' || order.status === 'complete';
    if (filter === 'pending') return order.status === 'pending';
    if (filter === 'failed') return order.status === 'failed';
    if (filter === 'guest') return order.userDetails && order.userDetails.isGuest === true;
    if (filter === 'registered') return !order.userDetails || order.userDetails.isGuest !== true;
    return true;
  });

  return (
    <div className="sales-tracking-page">
      {error && <div className="error-message">{error}</div>}

      <div className="tracking-container">
        <div className="tracking-header">
          <h1>Sales Tracking</h1>
          <p>Monitor and analyze sales data from all customer purchases</p>
        </div>
        
        <div className="filters-row">
          <div className="user-selector">
            <label htmlFor="user-select">Select User:</label>
            <select 
              id="user-select" 
              value={selectedUser} 
              onChange={handleUserChange}
              disabled={loading || users.length === 0}
            >
              <option value="">All Users</option>
              <optgroup label="Registered Users">
                {users
                  .filter(user => !user.isGuest)
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email || 'Unknown'} {user.email ? `(${user.email})` : ''}
                    </option>
                  ))
                }
              </optgroup>
              <optgroup label="Guest Users">
                {users
                  .filter(user => user.isGuest)
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || 'Guest'} {user.email ? `(${user.email})` : ''}
                    </option>
                  ))
                }
              </optgroup>
            </select>
          </div>
          
          <div className="filter-selector">
            <label htmlFor="filter-select">Filter Orders:</label>
            <select 
              id="filter-select" 
              value={filter} 
              onChange={handleFilterChange}
              disabled={loading}
            >
              <option value="all">All Orders</option>
              <option value="completed">Completed Orders</option>
              <option value="pending">Pending Orders</option>
              <option value="failed">Failed Orders</option>
              <option value="guest">Guest Purchases</option>
              <option value="registered">Registered User Purchases</option>
            </select>
          </div>
        </div>

        {loading && <div className="loading-spinner"></div>}

        {!loading && filteredOrders.length === 0 && (
          <div className="no-orders">No orders found matching the selected criteria.</div>
        )}

        {!loading && filteredOrders.length > 0 && (
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>User</th>
                  <th>Package</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => {
                  // Find user info for this order
                  const orderUser = users.find(u => u.id === order.userId) || {};
                  
                  return (
                    <tr key={order.id}>
                      <td>{order.id}</td>
                      <td>
                        {orderUser.name || order.userDetails?.name || 'Unknown'}
                        <div className="user-email">
                          {orderUser.email || order.userDetails?.email || 'No email'}
                          {(orderUser.isGuest || order.userDetails?.isGuest) && (
                            <span className="guest-badge">Guest</span>
                          )}
                        </div>
                      </td>
                      <td>{order.packageTitle}</td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>${order.totalPrice}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredOrders.length > 0 && (
          <div className="order-summary">
            <div className="summary-card">
              <h3>Total Orders</h3>
              <p className="summary-number">{filteredOrders.length}</p>
            </div>
            <div className="summary-card">
              <h3>Total Revenue</h3>
              <p className="summary-number">
                ${filteredOrders
                  .filter(order => order.status === 'completed' || order.status === 'complete')
                  .reduce((sum, order) => sum + (order.totalPrice || 0), 0)
                  .toFixed(2)}
              </p>
            </div>
            <div className="summary-card">
              <h3>Completed Orders</h3>
              <p className="summary-number">
                {filteredOrders.filter(order => 
                  order.status === 'complete' || order.status === 'completed'
                ).length}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesTracking; 